import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { datasetsApi } from '@data/rest/api';
import { retryUnlessRefused } from '@data/rest/retryUnlessRefused';

export const datasetKeys = Object.freeze({
    all: ['datasets'] as const,
    lists() {
        return [...datasetKeys.all, 'lists'] as const;
    },
    detail(datasetId: string) {
        return [...datasetKeys.all, 'detail', datasetId] as const;
    },
    versions(datasetId: string) {
        return [...datasetKeys.detail(datasetId), 'versions'] as const;
    },
    versionDownloadUrls(datasetId: string, versionId: string) {
        return ['datasetVersionDownloadUrls', datasetId, versionId] as const;
    },
});

export function useDatasets() {
    return useQuery(datasetKeys.lists(), () => datasetsApi.list(), {
        staleTime: 10_000,
        retry: retryUnlessRefused,
    });
}

export function useDataset(datasetId: string | null) {
    return useQuery(datasetKeys.detail(datasetId!), () => datasetsApi.get(datasetId!), {
        enabled: datasetId !== null,
        staleTime: 10_000,
        retry: retryUnlessRefused,
    });
}

export function useDatasetVersions(datasetId: string | null) {
    return useQuery(datasetKeys.versions(datasetId!), () => datasetsApi.listVersions(datasetId!), {
        enabled: datasetId !== null,
        staleTime: 10_000,
        retry: retryUnlessRefused,
    });
}

export function useDatasetVersionDownloadUrls(datasetId: string | null, versionId: string | null) {
    return useQuery(
        datasetKeys.versionDownloadUrls(datasetId!, versionId!),
        // Run links can point at a cancelled version
        () => datasetsApi.getVersionDownloadUrls(datasetId!, versionId!, true),
        {
            enabled: datasetId !== null && versionId !== null,
            // A hidden tab skips the interval, so focus refetches links that expired meanwhile
            refetchOnWindowFocus: true,
            retry: retryUnlessRefused,
            staleTime: 50 * 60 * 1000, // 50 minutes (signed URLs expire in 1 hour)
            refetchInterval: 50 * 60 * 1000,
        }
    );
}

// Each call signs fresh URLs. A signed URL answers with an attachment disposition, so the browser saves the file.
export function useSignDatasetVersionDownloadUrls(datasetId: string, versionId: string) {
    return useMutation(() => datasetsApi.getVersionDownloadUrls(datasetId, versionId, true));
}

export function useDeleteDataset() {
    const queryClient = useQueryClient();
    return useMutation((datasetId: string) => datasetsApi.deleteDataset(datasetId), {
        // Refetching the deleted dataset would answer 404 while its page is still open.
        onSuccess: (_data, datasetId) => queryClient.removeQueries(datasetKeys.detail(datasetId)),
        onError: (_error, datasetId) => queryClient.invalidateQueries(datasetKeys.detail(datasetId)),
        onSettled: () => queryClient.invalidateQueries(datasetKeys.lists()),
    });
}

export function usePromoteDatasetVersion(datasetId: string) {
    const queryClient = useQueryClient();
    return useMutation((versionId: string) => datasetsApi.promoteVersion(datasetId, versionId), {
        onSettled: () => queryClient.invalidateQueries(datasetKeys.all),
    });
}

export interface RestoreDatasetVersionRequest {
    versionId: string;
    expectedCurrentVersionId: string | null;
}

export function useRestoreDatasetVersion(datasetId: string) {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ versionId, expectedCurrentVersionId }: RestoreDatasetVersionRequest) => {
            try {
                // A null makes the server check that the dataset has no current version.
                return await datasetsApi.restoreVersion(datasetId, versionId, { expectedCurrentVersionId });
            } catch (e) {
                // A repeated click finds the version already current.
                if (e instanceof Response && e.status === 409) {
                    const conflict = await e
                        .clone()
                        .json()
                        .catch(() => null);
                    if (conflict?.actualCurrentVersionId === versionId) return datasetsApi.get(datasetId);
                }
                throw e;
            }
        },
        {
            onSettled: () => queryClient.invalidateQueries(datasetKeys.all),
        }
    );
}
